"use client";

import React from 'react';
import { motion } from 'framer-motion';
import { useComponentTranslations } from '@/app/components/LanguageProvider';
import { Icon } from '@iconify/react';
import ServicesAccordion from '@/app/components/ServicesAccordion';
import AlternatingUseCases from '@/app/components/AlternatingUseCases';
import TechSection from '@/components/industry/TechSection';
import { IndustrialsForm } from '@/app/components/IndustrialsForm';

const MarketingPage = () => {
    const { t, translationsLoaded } = useComponentTranslations('Marketing');
    const mt = (key: string, fallback?: string) => {
        if (!translationsLoaded) return fallback ?? key;
        return t(key, fallback) ?? fallback ?? key;
    };

    const expertiseItems = [
        { title: mt('marketingExpertise1'), icon: 'carbon:analytics' },
        { title: mt('marketingExpertise2'), icon: 'carbon:machine-learning-model' },
        { title: mt('marketingExpertise3'), icon: 'carbon:rule' },
        { title: mt('marketingExpertise4'), icon: 'carbon:security' },
        { title: mt('marketingExpertise5'), icon: 'carbon:cloud-app' },
        { title: mt('marketingExpertise6'), icon: 'carbon:blockchain' },
        { title: mt('marketingExpertise7'), icon: 'carbon:data-refinery' },
        { title: mt('marketingExpertise8'), icon: 'carbon:chart-line-data' },
        { title: mt('marketingExpertise9'), icon: 'carbon:user-profile' },
    ];

    const useCasesForAccordion = [
        { title: mt('marketingUseCase1'), description: mt('marketingUseCase1Desc'), backgroundImage: '/images/robot-with-laptop-computer-mouse_1048-3560.jpg' },
        { title: mt('marketingUseCase2'), description: mt('marketingUseCase2Desc'), backgroundImage: '/images/robot-with-shopping-cart_1048-3574.jpg' },
        { title: mt('marketingUseCase3'), description: mt('marketingUseCase3Desc'), backgroundImage: '/images/robot-with-clipboard-boxes_1048-3603.jpg' },
        { title: mt('marketingUseCase4'), description: mt('marketingUseCase4Desc'), backgroundImage: '/images/education_1.jpg' },
    ];

    const servicesForAlternating = [
        { title: mt('marketingService1Title'), description: mt('marketingService1Desc'), image: '/images/finance.jpg' },
        { title: mt('marketingService2Title'), description: mt('marketingService2Desc'), image: '/images/digital-marketing.jpg' },
        { title: mt('marketingService3Title'), description: mt('marketingService3Desc'), image: '/images/robot-constructor_1048-4570.jpg' },
        { title: mt('marketingService4Title'), description: mt('marketingService4Desc'), image: '/images/robot-with-clipboard_1048-3605.jpg' },
        { title: mt('marketingService5Title'), description: mt('marketingService5Desc'), image: '/images/robot-working-with-computer_1048-4282.jpg' },
    ];

    const faqItems = [
        { question: mt('marketingFaq1q'), answer: mt('marketingFaq1a') },
        { question: mt('marketingFaq2q'), answer: mt('marketingFaq2a') },
        { question: mt('marketingFaq3q'), answer: mt('marketingFaq3a') },
        { question: mt('marketingFaq4q'), answer: mt('marketingFaq4a') },
        { question: mt('marketingFaq5q'), answer: mt('marketingFaq5a') },
    ];

    return (
        <div className="relative min-h-screen bg-transparent text-white">
            {/* Hero-блок */}
            <header className="flex flex-col justify-center items-center text-center px-4 sm:px-6 lg:px-8 pt-24">
                <motion.h1
                    className="text-3xl lg:text-7xl font-bold tracking-tight mb-6 bg-linear-to-b from-white to-white/90 bg-clip-text text-transparent"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    style={{ willChange: "transform, opacity" }}
                >
                    {mt('marketingHeroTitle')}
                </motion.h1>
                <motion.p
                    className="text-lg text-white/70 max-w-3xl mx-auto pb-24"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    style={{ willChange: "transform, opacity" }}
                >
                    {mt('marketingHeroSubtitle')}
                </motion.p>
            </header>

            {/* Проблемы индустрии */}
            <section className="w-full py-24 glass-section glass-border-y">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 className="text-3xl font-bold text-center mb-12">{mt('marketingPainTitle')}</h2>
                    <p className="text-lg text-white/70 text-center max-w-4xl mx-auto">
                        {mt('marketingPainIntro')}
                    </p>
                </div>
            </section>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Наше решение / Expertise */}
                <section className="my-24">
                    <h2 className="text-3xl font-bold text-center mb-12">{mt('marketingExpertiseTitle')}</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {expertiseItems.map((item, index) => (
                            <motion.div
                                key={index}
                                className="p-6 glass-border-strong glass-rounded glass-card flex flex-col items-center text-center"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.3 }}
                                transition={{ duration: 0.4, delay: index * 0.05 }}
                                style={{ willChange: "transform, opacity" }}
                            >
                                <Icon icon={item.icon} className="text-4xl mb-4 text-blue-400" />
                                <h3 className="font-semibold">{item.title}</h3>
                            </motion.div>
                        ))}
                    </div>
                </section>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <AlternatingUseCases title={mt('marketingServicesTitle')} useCases={servicesForAlternating} />
            </div>

            {/* Accordion with Use Cases */}
            <ServicesAccordion title={mt('marketingUseCasesTitle')} services={useCasesForAccordion} />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <TechSection title={mt('marketingTechTitle')} t={t} />
                <IndustrialsForm label={mt('marketingCtaTitle')} />

                {/* FAQ */}
                <section className="my-24 max-w-4xl mx-auto">
                    <h2 className="text-3xl font-bold text-center mb-12">{mt('marketingFaqTitle')}</h2>
                    <div className="space-y-4">
                        {faqItems.map((item, index) => (
                            <details key={index} className="p-4 glass-border-strong glass-rounded glass-card">
                                <summary className="font-semibold cursor-pointer">{item.question}</summary>
                                <p className="mt-2 text-white/70">{item.answer}</p>
                            </details>
                        ))}
                    </div>
                </section>

            </div>
        </div>
    );
};

export default MarketingPage;
